import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, ArrowLeft, CheckCircle, Storefront, CircleNotch } from "@phosphor-icons/react";

function OrderResult({ result, preview }) {
  const o = result.order || {};
  const items = o.items || [];
  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-5" data-testid="willingdon-result">
      {preview && <img src={preview} alt="Order slip" className="lg:col-span-2 w-full rounded-md border border-zinc-200 object-contain max-h-[420px] bg-white" />}
      <div className="lg:col-span-3 rounded-md border border-zinc-200 bg-white p-5">
        <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold" data-testid="willingdon-saved">
          <CheckCircle size={18} weight="fill" /> Saved · ref {result.id}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-zinc-500">
          <div>Order #<div className="text-slate-100 font-mono text-sm">{o.order_no || "—"}</div></div>
          <div>Date<div className="text-slate-100 font-mono text-sm">{o.date || "—"}</div></div>
          <div>Table / Customer<div className="text-slate-100 text-sm">{o.customer_or_table || "—"}</div></div>
        </div>
        <table className="mt-4 w-full text-sm" data-testid="willingdon-items">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th>
              <th className="text-left py-2 pl-3">Unit</th><th className="text-right py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} className="py-4 text-center text-zinc-500">No items could be read. The photo is saved, so try a clearer shot.</td></tr>
            )}
            {items.map((it, i) => (
              <tr key={i} className="border-b border-zinc-100" data-testid={`willingdon-item-${i}`}>
                <td className="py-2 pr-2">{it.item}{it.notes && <div className="text-[11px] text-zinc-500">{it.notes}</div>}</td>
                <td className="py-2 text-right font-mono">{it.qty ?? "—"}</td>
                <td className="py-2 pl-3 text-zinc-600">{it.unit || ""}</td>
                <td className="py-2 text-right font-mono">{it.amount ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {o.total != null && <div className="mt-3 text-right font-mono font-bold" data-testid="willingdon-total">Total {o.total}</div>}
        {o.notes && <div className="mt-2 text-xs text-zinc-600">Notes: {o.notes}</div>}
      </div>
    </div>
  );
}

export default function Willingdon() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/willingdon/orders", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success("Order read and saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the order. Please try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="willingdon-page">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <button type="button" onClick={() => navigate("/")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5"
          data-testid="willingdon-back-btn">
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
        <div className="mt-8 flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center rounded-sm bg-zinc-900 text-white"><Storefront size={24} weight="duotone" /></div>
          <div>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl text-zinc-900">Willingdon</h1>
            <p className="text-sm text-zinc-500 mt-1">Take a photo of the order slip. It's read automatically and saved.</p>
          </div>
        </div>

        <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile}
          data-testid="willingdon-file-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={busy}
          className="mt-10 h-16 px-10 text-lg rounded-sm bg-red-600 hover:bg-red-500 text-white font-bold shadow-[0_8px_24px_rgba(220,38,38,0.25)]"
          data-testid="willingdon-read-order-btn">
          {busy ? <CircleNotch size={22} className="mr-2 animate-spin" /> : <Camera size={22} weight="bold" className="mr-2" />}
          {busy ? "Reading the order…" : result ? "Read Another Order" : "Read The Order"}
        </Button>

        {result && <OrderResult result={result} preview={preview} />}
      </div>
    </div>
  );
}
